import { HeartPulse, Home, Infinity, PiggyBank, ShieldCheck } from "lucide-react";
import type { ProductConfig, ProductId } from "./types";
import {
  computeDozycie,
  computeKredyt,
  computeOchronne,
  computeWsparcie,
  computeZdrowotne
} from "./rates";

const termOptions = (years: number[]) => years.map((y) => ({ value: String(y), label: `${y} lat` }));

export const PRODUCTS: Record<ProductId, ProductConfig> = {
  ochronne: {
    id: "ochronne",
    slug: "kalkulator-ubezpieczenia-na-zycie",
    leadProduct: "life",
    Icon: ShieldCheck,
    cardTitle: "Ochronne na życie",
    cardCopy: "Świadczenie dla bliskich na wypadek śmierci. Podstawowa ochrona terminowa.",
    heroKicker: "Kalkulator ubezpieczenia na życie",
    heroTitle: "Kalkulator ubezpieczenia na życie",
    heroCopy:
      "Sprawdź orientacyjną miesięczną składkę ubezpieczenia ochronnego na życie. Bez podawania danych osobowych — wynik zobaczysz od razu.",
    toolTitle: "Oblicz składkę ubezpieczenia na życie.",
    toolIntro:
      "Wariant ochronny wypłaca ustaloną sumę bliskim na wypadek Twojej śmierci. To podstawa zabezpieczenia rodziny, kredytu i wspólnych zobowiązań.",
    toolBullets: [
      "Składka od 45 zł/mies.; zależy od wieku, sumy, okresu i oceny ryzyka.",
      "Ochrona na całym świecie, 24 h/dobę — wypłata sumy ubezpieczenia bliskim.",
      "Możesz dołożyć ciężkie choroby, leczenie szpitalne i operacje (umowy dodatkowe)."
    ],
    fields: [
      { type: "slider", id: "age", label: "Wiek", min: 18, max: 65, step: 1, default: 35, format: "lata" },
      {
        type: "slider",
        id: "sum",
        label: "Suma ubezpieczenia",
        min: 50000,
        max: 2000000,
        step: 50000,
        default: 300000,
        format: "zl",
        help: "Od 50 000 zł; finalną sumę ogranicza ocena ryzyka."
      },
      {
        type: "select",
        id: "term",
        label: "Okres ochrony",
        options: termOptions([5, 10, 15, 20, 25, 30]),
        default: "20",
        help: "Od 5 do 30 lat, nie dłużej niż do roku, w którym kończysz 70 lat."
      },
      {
        type: "segmented",
        id: "coverage",
        label: "Zakres ochrony",
        options: [
          { value: "smierc", label: "Sama śmierć" },
          { value: "powazne", label: "+ Ciężkie choroby" },
          { value: "pelny", label: "Pełny pakiet zdrowotny" }
        ],
        default: "smierc",
        help: "Możliwe rozszerzenia obejmują m.in. ciężkie choroby, leczenie szpitalne, operacje i uszczerbek po wypadku."
      },
      { type: "toggle", id: "smoker", label: "Podwyższone ryzyko zdrowotne (np. palenie)", default: false },
      {
        type: "segmented",
        id: "lz",
        label: "Leczenie za granicą (onkologia)",
        options: [
          { value: "bez", label: "Bez" },
          { value: "standard", label: "Standard" },
          { value: "premium", label: "Premium" }
        ],
        default: "bez",
        help: "Organizacja i pokrycie leczenia onkologicznego za granicą: Standard do 1 mln € (świat bez USA), Premium do 2 mln € (cały świat). Składka z realnej taryfy PZU wg wieku."
      }
    ],
    compute: computeOchronne,
    ctaLabel: "Poproś o dokładną kalkulację",
    metaTitle: "Kalkulator ubezpieczenia na życie 2026 - oblicz składkę online | Ochro.pl",
    metaDescription:
      "Kalkulator ubezpieczenia na życie: podaj wiek, sumę i okres, a sprawdzisz orientacyjną miesięczną składkę polisy ochronnej. Bez podawania danych.",
    faqs: [
      {
        question: "Od czego zależy składka ubezpieczenia ochronnego?",
        answer:
          "Od wieku polisowego, sumy ubezpieczenia, okresu ochrony, częstotliwości opłacania składki oraz wyniku oceny ryzyka. Składka minimalna to 45 zł miesięcznie."
      },
      {
        question: "Jaki wiek i okres ochrony obejmuje produkt?",
        answer:
          "Wiek wstępu od 18 do 65 lat. Okres ochrony od 5 do 30 lat, nie dłużej niż do rocznicy umowy w roku, w którym kończysz 70 lat."
      },
      {
        question: "Czy kalkulator pokazuje finalną cenę?",
        answer:
          "Nie. Wynik jest orientacyjny. Ostateczna składka zależy od oceny ryzyka (ankieta medyczna, czasem badania), zakresu i ewentualnych zwyżek."
      }
    ]
  },

  dozycie: {
    id: "dozycie",
    slug: "kalkulator-ubezpieczenia-na-zycie-i-dozycie",
    leadProduct: "endowment",
    Icon: PiggyBank,
    cardTitle: "Na życie i dożycie",
    cardCopy: "Ochrona życia połączona z gromadzeniem kapitału i premiami na przyszłość.",
    heroKicker: "Kalkulator na życie i dożycie",
    heroTitle: "Kalkulator ubezpieczenia na życie i dożycie",
    heroCopy:
      "Połącz ochronę życia z odkładaniem kapitału. Ustaw sumę gwarantowaną i okres, a sprawdzisz orientacyjną składkę oraz szacowaną wypłatę z premiami.",
    toolTitle: "Oblicz składkę polisy na życie i dożycie.",
    toolIntro:
      "Ten wariant łączy 3 elementy: ochronę na wypadek śmierci, gromadzenie kapitału i szansę na premie. Na koniec umowy otrzymujesz zgromadzoną sumę, a kwotę gwarantowaną znasz już przy podpisaniu.",
    toolBullets: [
      "Ustal sumę gwarantowaną i okres — policzymy orientacyjną składkę (od 200 zł/mies.).",
      "Wariant Konserwatywny (100% obligacje skarbowe) lub Umiarkowany (wyższy potencjał zysku).",
      "Suma trafia do bliskich przy śmierci albo do Ciebie na koniec umowy; premie ją powiększają."
    ],
    fields: [
      {
        type: "segmented",
        id: "wariant",
        label: "Wariant",
        options: [
          { value: "konserwatywny", label: "Konserwatywny" },
          { value: "umiarkowany", label: "Umiarkowany" }
        ],
        default: "konserwatywny",
        help: "Konserwatywny — środki w 100% w obligacjach skarbowych, premia co roku.\nUmiarkowany — część w innych instrumentach, wyższy potencjalny zysk, premia co 3 lata."
      },
      { type: "slider", id: "age", label: "Wiek", min: 18, max: 65, step: 1, default: 40, format: "lata" },
      { type: "slider", id: "term", label: "Okres ubezpieczenia", min: 10, max: 30, step: 1, default: 15, format: "lata" },
      {
        type: "slider",
        id: "sum",
        label: "Suma ubezpieczenia (kapitał gwarantowany)",
        min: 15000,
        max: 500000,
        step: 5000,
        default: 50000,
        format: "zl",
        help: "Kwota, którą gwarantujemy: wypłata dla bliskich przy śmierci albo dla Ciebie na koniec umowy. Składka min. 200 zł/mies."
      }
    ],
    compute: computeDozycie,
    ctaLabel: "Poproś o symulację polisy",
    metaTitle: "Kalkulator ubezpieczenia na życie i dożycie | Ochro.pl",
    metaDescription:
      "Kalkulator polisy na życie i dożycie z premiami: ustaw sumę gwarantowaną i okres, a sprawdzisz orientacyjną miesięczną składkę oraz szacowaną wypłatę z premiami.",
    faqs: [
      {
        question: "Czym różni się polisa na dożycie od zwykłej ochronnej?",
        answer:
          "Na życie i dożycie łączy ochronę (wypłata dla bliskich przy śmierci) z gromadzeniem kapitału, który otrzymujesz na koniec umowy. Polisa ochronna nie gromadzi kapitału."
      },
      {
        question: "Co to są premie i waloryzacja?",
        answer:
          "Premie to dodatkowa suma ubezpieczenia przyznawana w trakcie umowy (raz przyznana, nie jest odbierana). Waloryzacja podnosi składkę i sumę, by ograniczyć wpływ inflacji. Dzięki nim wypłata końcowa bywa wyższa od sumy gwarantowanej."
      },
      {
        question: "Jaka jest minimalna składka i okres?",
        answer:
          "Składka regularna od 200 zł miesięcznie. Okres w wariancie Konserwatywnym od 10 do 30 lat, w Umiarkowanym od 12 do 30 lat."
      }
    ]
  },

  zdrowotne: {
    id: "zdrowotne",
    slug: "kalkulator-ubezpieczenia-zdrowotnego",
    leadProduct: "health",
    Icon: HeartPulse,
    cardTitle: "Pakiet zdrowotny",
    cardCopy: "Prywatna opieka medyczna: konsultacje specjalistów, diagnostyka, telekonsultacje.",
    heroKicker: "Kalkulator ubezpieczenia zdrowotnego",
    heroTitle: "Kalkulator ubezpieczenia zdrowotnego",
    heroCopy:
      "Sprawdź orientacyjną składkę pakietu prywatnej opieki medycznej dla siebie lub rodziny. Wybierz zakres i liczbę osób.",
    toolTitle: "Oblicz składkę pakietu zdrowotnego.",
    toolIntro:
      "Pakiet zdrowotny daje dostęp do prywatnej opieki w ponad 2400 placówkach: telekonsultacje, konsultacje specjalistów i diagnostyka, bez kolejek. Ceny zaczynają się od ok. 597 zł rocznie dla jednej osoby.",
    toolBullets: [
      "Wybierz zakres (Mini → Premium) i dla kogo: siebie, partnera, rodziny czy dziecka.",
      "Im szerszy pakiet, tym więcej specjalistów i badań bez limitów.",
      "Pakiet opłaca się rocznie z góry (dostępne też raty 0%)."
    ],
    fields: [
      {
        type: "select",
        id: "kto",
        label: "Dla kogo",
        options: [
          { value: "ja", label: "Dla Ciebie" },
          { value: "para", label: "Dla pary" },
          { value: "rodzina", label: "Dla rodziny" },
          { value: "dziecko", label: "Dla Ciebie i dziecka" },
          { value: "senior", label: "Dla seniora 67+" },
          { value: "student", label: "Dla studenta 18–27" }
        ],
        default: "ja"
      },
      {
        type: "segmented",
        id: "zakres",
        label: "Zakres pakietu",
        options: [
          { value: "mini", label: "Mini" },
          { value: "podstawowy", label: "Podstawowy" },
          { value: "optymalny", label: "Optymalny" },
          { value: "premium", label: "Premium" }
        ],
        default: "podstawowy",
        help: "Mini — telekonsultacje i podstawowi specjaliści; Premium — pełny zakres specjalistów i diagnostyki."
      }
    ],
    compute: computeZdrowotne,
    ctaLabel: "Poproś o kalkulację pakietu medycznego",
    metaTitle: "Kalkulator ubezpieczenia zdrowotnego - pakiet medyczny online | Ochro.pl",
    metaDescription:
      "Kalkulator ubezpieczenia zdrowotnego: wybierz zakres pakietu i liczbę osób, a sprawdzisz orientacyjną składkę prywatnej opieki medycznej.",
    faqs: [
      {
        question: "Ile kosztuje pakiet zdrowotny?",
        answer:
          "Dla jednej osoby ceny zaczynają się od ok. 597 zł rocznie (Mini), przez ok. 1490 zł (Podstawowy) i 3044 zł (Optymalny) do 5433 zł (Premium). Warianty rodzinne i dla dziecka są droższe, ale często z rabatem."
      },
      {
        question: "Co obejmuje prywatny pakiet medyczny?",
        answer:
          "Telekonsultacje, konsultacje lekarzy specjalistów oraz badania diagnostyczne i laboratoryjne w ponad 2400 placówkach. Zakres rośnie wraz z wariantem (Mini → Premium)."
      },
      {
        question: "Czy mogę objąć pakietem rodzinę lub dziecko?",
        answer:
          "Tak. Dostępne są warianty dla pary, rodziny, Ciebie i dziecka, seniora (67+) oraz studenta. Cena zależy od liczby osób i wybranego zakresu."
      }
    ]
  },

  kredyt: {
    id: "kredyt",
    slug: "kalkulator-ubezpieczenia-na-zycie-pod-kredyt",
    leadProduct: "mortgage",
    Icon: Home,
    cardTitle: "Pod kredyt hipoteczny",
    cardCopy: "Polisa z cesją na bank, dopasowana do salda i okresu kredytu hipotecznego.",
    heroKicker: "Kalkulator ubezpieczenia pod kredyt",
    heroTitle: "Kalkulator ubezpieczenia na życie pod kredyt",
    heroCopy:
      "Sprawdź orientacyjną składkę polisy na życie pod kredyt hipoteczny. Sumę dopasujesz do kwoty kredytu, a okres do harmonogramu spłaty.",
    toolTitle: "Oblicz składkę polisy pod kredyt.",
    toolIntro:
      "Dopasuj sumę do kredytu hipotecznego i sprawdź orientacyjną składkę polisy, którą można omówić z doradcą pod wymagania banku.",
    toolBullets: [
      "Suma ubezpieczenia = saldo kredytu, okres = okres kredytu (maks. 30 lat).",
      "Po wpisie hipoteki czasem można dopasować sumę do finalnego salda kredytu.",
      "Zewnętrzna polisa z cesją bywa tańsza niż w banku i zostaje po spłacie kredytu."
    ],
    fields: [
      {
        type: "slider",
        id: "sum",
        label: "Kwota kredytu (suma ubezpieczenia)",
        min: 50000,
        max: 1500000,
        step: 25000,
        default: 400000,
        format: "zl",
        help: "Najczęściej suma odpowiada aktualnemu saldu kredytu. Po wpisie hipoteki można czasem dopasować ją do finalnego salda albo zostawić dodatkowy bufor dla rodziny."
      },
      {
        type: "slider",
        id: "term",
        label: "Okres kredytu",
        min: 5,
        max: 30,
        step: 1,
        default: 25,
        format: "lata",
        help: "Zwykle okres polisy ustawia się pod okres spłaty kredytu. W kalkulatorze maksymalnie 30 lat, nie dłużej niż do rocznicy w roku, w którym kończysz 70 lat."
      },
      { type: "slider", id: "age", label: "Wiek kredytobiorcy", min: 18, max: 65, step: 1, default: 35, format: "lata" },
      {
        type: "segmented",
        id: "coverage",
        label: "Zakres ochrony",
        options: [
          { value: "smierc", label: "Sama śmierć" },
          { value: "powazne", label: "+ Ciężkie choroby (CC)" },
          { value: "niezdolnosc", label: "+ Utrata sprawności (US)" }
        ],
        default: "smierc"
      },
      {
        type: "select",
        id: "bank",
        label: "Bank (cesja)",
        options: [
          { value: "pko", label: "PKO BP" },
          { value: "pekao", label: "Pekao" },
          { value: "santander", label: "Santander" },
          { value: "mbank", label: "mBank" },
          { value: "ing", label: "ING" },
          { value: "millennium", label: "Millennium" },
          { value: "alior", label: "Alior" },
          { value: "inny", label: "Inny / jeszcze nie wiem" }
        ],
        default: "inny",
        help: "Przy zewnętrznej polisie bank zwykle wymaga cesji. Taki wariant bywa tańszy niż polisa bankowa, ale przed zmianą trzeba sprawdzić wymagania banku i warunki kredytu."
      },
      { type: "toggle", id: "smoker", label: "Podwyższone ryzyko zdrowotne (np. palenie)", default: false }
    ],
    compute: computeKredyt,
    ctaLabel: "Poproś o polisę pod kredyt",
    metaTitle: "Kalkulator polisy na życie pod kredyt hipoteczny i cesję | Ochro.pl",
    metaDescription:
      "Kalkulator polisy na życie pod kredyt hipoteczny: dopasuj sumę do salda, okres do spłaty i sprawdź orientacyjną składkę z cesją na bank.",
    faqs: [
      {
        question: "Czy polisa pod kredyt musi być z banku?",
        answer:
          "Nie zawsze. Często można użyć zewnętrznej polisy z cesją na bank, zwykle taniej. Trzeba jednak spełnić wymagania banku co do sumy, zakresu i okresu ochrony."
      },
      {
        question: "Czy zewnętrzna polisa może zastąpić polisę bankową?",
        answer:
          "Często tak, jeżeli bank akceptuje zakres, sumę, okres i cesję. Przed rezygnacją z wariantu bankowego trzeba sprawdzić warunki kredytu, wymaganą dokumentację i ewentualny wpływ na marżę."
      },
      {
        question: "Ile kosztuje polisa na życie pod kredyt?",
        answer:
          "Składka zaczyna się od 45 zł miesięcznie i zależy od sumy (salda kredytu), okresu, wieku i oceny ryzyka. Wynik kalkulatora jest orientacyjny."
      },
      {
        question: "Na jaką sumę ubezpieczyć kredyt?",
        answer:
          "Zwykle na aktualne saldo kredytu. Część osób ubezpiecza się na nieco wyższą kwotę, aby zostawić bliskim zapas po spłacie zobowiązania."
      },
      {
        question: "Co przygotować do rozmowy o polisie pod kredyt?",
        answer:
          "Przyda się kwota kredytu lub aktualne saldo, okres pozostały do spłaty, nazwa banku, liczba kredytobiorców, wiek oraz informacja, czy ochrona ma objąć tylko śmierć, czy też dodatki chorobowe."
      }
    ]
  },

  wsparcie: {
    id: "wsparcie",
    slug: "kalkulator-ubezpieczenia-na-cale-zycie",
    leadProduct: "wholelife",
    Icon: Infinity,
    cardTitle: "Dożywotnie na życie",
    cardCopy: "Ochrona na całe życie — gwarantowana wypłata dla bliskich, składka płacona czasowo.",
    heroKicker: "Kalkulator ubezpieczenia na całe życie",
    heroTitle: "Kalkulator dożywotniego ubezpieczenia na życie",
    heroCopy:
      "Ochrona, która działa do końca życia, choć składkę płacisz tylko przez określony czas. Sprawdź orientacyjną miesięczną składkę.",
    toolTitle: "Oblicz składkę dożywotniej polisy na życie.",
    toolIntro:
      "Polisa dożywotnia wypłaca sumę ubezpieczenia bliskim niezależnie od tego, kiedy nastąpi śmierć. Składki opłacasz przez określony czas zależny od wieku, a ochrona trwa dożywotnio.",
    toolBullets: [
      "Ochrona dożywotnia; składkę płacisz tylko do ustalonego wieku (np. do 70 lat).",
      "Gwarantowana suma dla bliskich, zwolniona z podatku od spadków i darowizn.",
      "Składka od 125 zł/mies.; zależy od wieku, sumy i oceny ryzyka."
    ],
    fields: [
      { type: "slider", id: "age", label: "Wiek", min: 13, max: 80, step: 1, default: 40, format: "lata" },
      {
        type: "slider",
        id: "sum",
        label: "Suma ubezpieczenia",
        min: 20000,
        max: 1000000,
        step: 10000,
        default: 150000,
        format: "zl",
        help: "Bez ustalonej górnej granicy (ogranicza ją ocena ryzyka). Do 30 000 zł zwykle wystarczy oświadczenie o stanie zdrowia."
      },
      { type: "toggle", id: "smoker", label: "Podwyższone ryzyko zdrowotne (np. palenie)", default: false }
    ],
    compute: computeWsparcie,
    ctaLabel: "Poproś o kalkulację polisy dożywotniej",
    metaTitle: "Kalkulator ubezpieczenia na całe życie | Ochro.pl",
    metaDescription:
      "Kalkulator dożywotniego ubezpieczenia na życie: podaj wiek i sumę, a sprawdzisz orientacyjną miesięczną składkę ochrony na całe życie.",
    faqs: [
      {
        question: "Czym różni się ubezpieczenie dożywotnie od terminowego?",
        answer:
          "Dożywotnie (na całe życie) wypłaca sumę zawsze, niezależnie od tego, kiedy nastąpi śmierć — składkę płacisz tylko przez określony czas. Terminowe chroni przez wybrany okres (np. 20 lat) i jest tańsze."
      },
      {
        question: "Jak długo płaci się składkę?",
        answer:
          "Zależnie od wieku przy zawarciu: wstępując do 45. r.ż. płacisz do 70 lat, 46–55 do 75 lat, 56–60 do 80 lat, 61–65 do 85 lat, a od 66 lat przez 15 lat. Potem ochrona trwa bez składek."
      },
      {
        question: "Jaki wiek obejmuje produkt?",
        answer:
          "Ubezpieczyć można osoby od 13 do 80 lat. Przy sumie do 30 000 zł zwykle wystarczy oświadczenie o stanie zdrowia, bez pełnej ankiety."
      }
    ]
  }
};

/** Kolejność produktów na hubie (kafelki). */
export const PRODUCT_ORDER: ProductId[] = ["ochronne", "dozycie", "zdrowotne", "kredyt", "wsparcie"];

export const PRODUCT_LIST = PRODUCT_ORDER.map((id) => PRODUCTS[id]);

/** Opcje pola „Temat rozmowy” w formularzu leada. Wartości muszą być zgodne z
 *  `leadProduct` produktów oraz z `allowedProducts` w functions/api/lead.ts. */
export const LEAD_PRODUCT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "life", label: "Ubezpieczenie na życie (ochronne)" },
  { value: "endowment", label: "Na życie i dożycie (oszczędnościowe)" },
  { value: "addons", label: "Umowy dodatkowe do polisy" },
  { value: "health", label: "Pakiet zdrowotny / opieka medyczna" },
  { value: "mortgage", label: "Polisa na życie pod kredyt" },
  { value: "wholelife", label: "Dożywotnie na życie" }
];
